/**
 * OCR API Client - Connects to OCR service (port 8001)
 */

import { supabase } from './supabase';
import { ApiError } from './apiErrors';

const OCR_API_URL = import.meta.env.VITE_OCR_API_URL || 'http://localhost:8001';

interface FetchOptions extends Omit<RequestInit, 'body'> {
  params?: Record<string, string>;
  body?: unknown;
}

class OcrApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      return {};
    }
    return {
      Authorization: `Bearer ${session.access_token}`,
    };
  }

  private async request<T>(
    endpoint: string,
    options: FetchOptions = {}
  ): Promise<T> {
    const { params, body, headers: customHeaders, ...fetchOptions } = options;

    let url = `${this.baseUrl}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    const authHeaders = await this.getAuthHeaders();

    const response = await fetch(url, {
      ...fetchOptions,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...customHeaders,
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const detail = errorData.detail;
      const message = errorData.message
        || (typeof detail === 'string' ? detail : detail?.message || detail?.error)
        || `OCR API error: ${response.status}`;
      throw new ApiError(
        response.status,
        message,
        errorData.code,
        errorData.details,
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  async get<T>(endpoint: string, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data,
    });
  }

  async patch<T>(endpoint: string, data?: unknown, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data,
    });
  }

  async delete<T>(endpoint: string, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  // Health check
  async health(): Promise<{ status: string }> {
    const response = await fetch(`${this.baseUrl}/health`);
    return response.json();
  }
}

export const ocrApi = new OcrApiClient(OCR_API_URL);

// TypeScript types for OCR API
export interface OcrJobProgress {
  total_images: number;
  processed_images: number;
  percent_complete: number;
}

export interface OcrJobResults {
  total_records: number;
  needs_review: number;
  ready_to_import: number;
}

export interface OcrJob {
  id: string;
  farm_id: string;
  status: 'pending' | 'processing' | 'complete' | 'failed' | 'cancelled';
  progress: OcrJobProgress;
  results?: OcrJobResults;
  error_message?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

export interface OcrJobListResponse {
  jobs: OcrJob[];
  total: number;
}

export interface OcrExtractedRecord {
  id: string;
  job_id: string;
  image_id?: string;
  record_type: 'water_reading' | 'feeding' | 'sampling' | 'harvest' | 'application';
  extracted_data: Record<string, unknown>;
  confidence_score: number;
  needs_review: boolean;
  review_completed: boolean;
  reviewed_by?: string;
  reviewed_at?: string;
  corrected_data?: Record<string, unknown>;
  imported: boolean;
  imported_at?: string;
  skipped?: boolean;
  skip_reason?: string;
  created_at: string;
}

export interface OcrRecordsListResponse {
  records: OcrExtractedRecord[];
  total: number;
  needs_review_count: number;
}

export interface OcrCycleOption {
  id: string;
  pond_id: string;
  pond_name: string;
  stocking_date: string;
}

export interface OcrImportPreview {
  total_records: number;
  by_type: Record<string, number>;
  validation_issues: Array<{ record_id: string; issues: string[] }>;
  duplicates_detected: number;
  ready_to_import: number;
}

export interface OcrImportResult {
  imported: Array<{ staging_id: string; main_id: string; record_type: string }>;
  failed: Array<{ staging_id: string; errors: string[] }>;
  total_imported: number;
  total_failed: number;
}

export interface OcrJobStats {
  total_records: number;
  imported_count: number;
  skipped_count: number;
  pending_review: number;
  reviewed_count: number;
  avg_confidence: number;
  by_type: Record<string, { total: number; imported: number }>;
}
